"use client";

import {useGSAP} from "@gsap/react";
import {gsap} from "gsap";
import {createContext, useContext, useMemo, useRef, useState, type ReactNode} from "react";
import {LOCALE_COOKIE, type Locale, localeDisplayName} from "./locales";

gsap.registerPlugin(useGSAP);
import {formatMessage, type MessageKey} from "./messages";

type I18nContextValue = {locale: Locale; setLocale: (locale: Locale) => void; t: (key: MessageKey, values?: Record<string, string | number>) => string};
const defaultI18n: I18nContextValue = {locale: "zh-CN", setLocale: () => undefined, t: (key, values) => formatMessage("zh-CN", key, values)};
const I18nContext = createContext<I18nContextValue>(defaultI18n);

/** 客户端只从服务端传入的稳定初始值开始渲染，切换仅更新文案和 Cookie，不触发业务请求。 */
export function LocaleProvider({initialLocale, children}: {initialLocale: Locale; children: ReactNode}) {
  const [locale, setLocaleState] = useState(initialLocale);
  const setLocale = (next: Locale) => {
    setLocaleState(next);
    document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(next)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    document.documentElement.lang = next === "en" ? "en" : next === "zh-TW" ? "zh-Hant" : "zh-Hans";
  };
  const value = useMemo(() => ({locale, setLocale, t: (key: MessageKey, values?: Record<string, string | number>) => formatMessage(locale, key, values)}), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** 三语切换胶囊：选中背景只做位移，避免切换时改变布局造成页面抖动。 */
export function LanguageSwitcher() {
  const {locale, setLocale, t} = useI18n();
  const switcherRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLSpanElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const locales = ["zh-CN", "zh-TW", "en"] as const;
  const activeIndex = locales.indexOf(locale);
  const {contextSafe} = useGSAP(() => {
    const thumb = thumbRef.current;
    const activeButton = switcherRef.current?.querySelector<HTMLButtonElement>(`button[data-locale-index="${activeIndex}"]`);
    if (!thumb || !activeButton) return;

    // 首屏按真实按钮位置落位；后续动画从当前 transform 连续移动，不重新计算起点。
    gsap.set(thumb, {x: activeButton.offsetLeft - 3, scaleX: 1});
  }, {scope: switcherRef});

  const handleLocaleChange = (item: Locale, index: number) => {
    if (item === locale && !gsap.isTweening(thumbRef.current)) return;
    contextSafe(() => {
      const root = switcherRef.current;
      const trail = trailRef.current;
      const thumb = thumbRef.current;
      const targetButton = root?.querySelector<HTMLButtonElement>(`button[data-locale-index="${index}"]`);
      const currentButton = root?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]');
      if (!trail || !thumb || !targetButton) return;

      const currentX = Number(gsap.getProperty(thumb, "x")) || 0;
      const targetX = targetButton.offsetLeft - 3;
      const movingForward = targetX > currentX;
      gsap.killTweensOf([trail, thumb, currentButton, targetButton]);

      // 所有用户都保留完整的空间反馈；语言内容仍等水滴抵达后再更新。
      // 拖尾和主圆滴使用同一段连续位移；从“简”到“EN”会自然穿过“繁”，但不会在中间停顿。
      if (currentButton) gsap.set(currentButton, {color: "#1677ff"});
      gsap.set(targetButton, {color: "#7d8190"});
      gsap.set(trail, {
        x: currentX,
        autoAlpha: 0.68,
        scaleX: 1,
        scaleY: 0.72,
        transformOrigin: movingForward ? "right center" : "left center",
      });
      const timeline = gsap.timeline({
        onComplete: () => {
          gsap.set(trail, {autoAlpha: 0, scaleX: 1, scaleY: 1});
          gsap.set([currentButton, targetButton].filter((button): button is HTMLButtonElement => Boolean(button)), {clearProps: "color"});
          // 语言内容必须等水滴抵达后再更新，避免文字先跳变而削弱滑动反馈。
          setLocale(item);
        },
      });
      timeline
        .to(trail, {x: targetX, scaleX: 1.65, scaleY: 0.78, autoAlpha: 0.5, duration: 0.64, ease: "power2.inOut"})
        .to(thumb, {x: targetX, scaleX: 1.12, scaleY: 0.94, duration: 0.64, ease: "power2.inOut"}, "<");
      if (currentButton) timeline.to(currentButton, {color: "#7d8190", duration: 0.22, ease: "power1.out"}, "<0.12");
      timeline
        .to(targetButton, {color: "#1677ff", duration: 0.22, ease: "power1.out"}, "-=0.2")
        .to(trail, {x: targetX, scaleX: 0.35, scaleY: 0.55, autoAlpha: 0, duration: 0.22, ease: "power2.in"}, "-=0.14")
        .to(thumb, {scaleX: 1, scaleY: 1, duration: 0.24, ease: "elastic.out(1, 0.55)"}, "-=0.18");
    })();
  };

  return <div ref={switcherRef} className="language-switcher" role="group" aria-label={t("locale.switcher")}>
    <span ref={trailRef} className="language-switcher-trail" aria-hidden="true" />
    <span ref={thumbRef} className="language-switcher-thumb" aria-hidden="true" />
    {locales.map((item, index) => <button key={item} type="button" data-locale-index={index} className={locale === item ? "is-selected" : ""} aria-pressed={locale === item} aria-label={t(`locale.${item}` as MessageKey)} onClick={() => handleLocaleChange(item, index)}>{localeDisplayName(item)}</button>)}
  </div>;
}
